import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Send, Loader2, Users, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PushTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [title, setTitle] = useState('Outlaw Media - test push');
  const [body, setBody] = useState('Push notifications are wired up correctly.');
  const [url, setUrl] = useState('/');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/push/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      setError(`Couldn't load subscriber count: ${e.message}`);
    }
  }, [token]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const sendTest = async () => {
    setSending(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API}/api/push/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, body, url }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
      loadStats();
    } catch (e) {
      setError(e.message || 'Failed to send test push.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="push-tab" className="space-y-6">
      <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="h-5 w-5 text-red-500" />
          <h3 className="text-xl font-black text-white">Push Notifications</h3>
        </div>

        <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-lg bg-zinc-950/60 border border-zinc-800">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-400">Active subscribers</span>
          <span
            data-testid="push-subscribers-count"
            className="ml-auto text-2xl font-black text-white"
          >
            {stats ? stats.subscribers.toLocaleString() : '-'}
          </span>
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Title</label>
            <input
              data-testid="push-test-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Body</label>
            <textarea
              data-testid="push-test-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={240}
              rows={2}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none resize-none"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Click-through URL</label>
            <input
              data-testid="push-test-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/explicit or https://…"
              maxLength={500}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-white text-sm outline-none"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={sendTest}
          disabled={sending || !title.trim() || !body.trim() || (stats && stats.subscribers === 0)}
          data-testid="push-test-send"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-black text-sm uppercase tracking-wider shadow-[0_0_18px_rgba(220,38,38,0.45)] transition-all"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          {sending ? 'Broadcasting…' : 'Send Test Push'}
        </button>

        {stats && stats.subscribers === 0 && (
          <p className="text-xs text-yellow-500/80 mt-3">
            No subscribers yet - install the PWA or accept the on-site prompt first.
          </p>
        )}

        {result && (
          <div
            data-testid="push-test-result"
            className="mt-4 flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-sm"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-emerald-300">Broadcast complete</p>
              <p className="text-emerald-200/80 text-xs mt-1">
                Delivered: <strong className="text-white">{result.sent}</strong>
                {' · '}Dead endpoints purged: <strong className="text-white">{result.removed}</strong>
                {result.reason ? ` · ${result.reason}` : ''}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div
            data-testid="push-test-error"
            className="mt-4 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-950/40 border border-red-500/30 text-sm"
          >
            <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-red-300">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushTab;
